/**
 * Renders a single chat message with markdown support and tool call cards.
 */

import React from 'react';
import { Bot, User } from 'lucide-react';
import DOMPurify from 'dompurify';
import ChatToolCard from './ChatToolCard';

function SimpleMarkdown({ text }) {
  if (!text) return null;

  // Basic markdown rendering: bold, inline code, headers, bullets, tables
  const lines = text.split('\n');
  const elements = [];
  let inTable = false;
  let tableRows = [];

  const escapeHtml = (str) =>
    str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const processInline = (line) => {
    line = escapeHtml(line);
    line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    line = line.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-white/10 rounded text-[#3EF1C7] text-xs">$1</code>');
    return DOMPurify.sanitize(line);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table detection
    if (line.includes('|') && line.trim().startsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      // Skip separator rows
      if (/^\|[\s-|]+\|$/.test(line.trim())) continue;
      const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
      tableRows.push(cells);
      continue;
    } else if (inTable) {
      inTable = false;
      elements.push(
        <div key={`table-${i}`} className="overflow-x-auto my-2">
          <table className="text-xs border-collapse w-full">
            <thead>
              <tr className="border-b border-white/10">
                {tableRows[0]?.map((cell, j) => (
                  <th key={j} className="px-2 py-1 text-left text-white/60 font-medium">{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(1).map((row, ri) => (
                <tr key={ri} className="border-b border-white/5">
                  {row.map((cell, j) => (
                    <td key={j} className="px-2 py-1" dangerouslySetInnerHTML={{ __html: processInline(cell) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Headers
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="text-sm font-semibold mt-3 mb-1" dangerouslySetInnerHTML={{ __html: processInline(line.slice(4)) }} />);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="text-base font-semibold mt-3 mb-1" dangerouslySetInnerHTML={{ __html: processInline(line.slice(3)) }} />);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="text-lg font-bold mt-3 mb-1" dangerouslySetInnerHTML={{ __html: processInline(line.slice(2)) }} />);
    }
    // Bullets
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-[#3EF1C7] mt-1">•</span>
          <span dangerouslySetInnerHTML={{ __html: processInline(line.slice(2)) }} />
        </div>
      );
    }
    // Numbered lists
    else if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)\./)[1];
      elements.push(
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-[#3EF1C7] font-medium min-w-[1.2em]">{num}.</span>
          <span dangerouslySetInnerHTML={{ __html: processInline(line.replace(/^\d+\.\s/, '')) }} />
        </div>
      );
    }
    // Empty lines
    else if (!line.trim()) {
      elements.push(<div key={i} className="h-2" />);
    }
    // Regular text
    else {
      elements.push(<p key={i} dangerouslySetInnerHTML={{ __html: processInline(line) }} />);
    }
  }

  // Flush remaining table
  if (inTable && tableRows.length) {
    elements.push(
      <div key="table-end" className="overflow-x-auto my-2">
        <table className="text-xs border-collapse w-full">
          <thead>
            <tr className="border-b border-white/10">
              {tableRows[0]?.map((cell, j) => (
                <th key={j} className="px-2 py-1 text-left text-white/60 font-medium">{cell}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.slice(1).map((row, ri) => (
              <tr key={ri} className="border-b border-white/5">
                {row.map((cell, j) => (
                  <td key={j} className="px-2 py-1" dangerouslySetInnerHTML={{ __html: processInline(cell) }} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return <div className="space-y-1 text-sm leading-relaxed">{elements}</div>;
}

export default function ChatMessage({ message }) {
  const isUser = message.role === 'user';
  const toolCalls = message.toolCalls || [];

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
        isUser ? 'bg-white/10' : 'bg-[#3EF1C7]/20'
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4 text-[#3EF1C7]" />}
      </div>

      {/* Content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        {/* Status indicator */}
        {message.status && (
          <div className="text-xs text-[#3EF1C7]/60 mb-1 animate-pulse">{message.status}</div>
        )}

        {/* Tool calls */}
        {toolCalls.length > 0 && (
          <div className="mb-2">
            {toolCalls.map((tc, i) => (
              <ChatToolCard key={i} toolCall={tc} />
            ))}
          </div>
        )}

        {/* Text content */}
        {message.content && (
          <div className={`inline-block px-4 py-3 rounded-2xl ${
            isUser
              ? 'bg-[#3EF1C7]/20 text-white'
              : 'bg-white/5 text-white/90'
          }`}>
            {isUser ? (
              <p className="text-sm">{message.content}</p>
            ) : (
              <SimpleMarkdown text={message.content} />
            )}
          </div>
        )}

        {/* Deep review badge */}
        {message.mode === 'deep_review' && (
          <div className="mt-1 text-xs text-amber-400/60">Opus 4.6 Deep Review</div>
        )}
      </div>
    </div>
  );
}
